export const resourcesData = [
  {
    id: "recognizing-early-relapse-signs",
    slug: "recognizing-early-relapse-signs",
    category: "Addiction & Recovery",
    title: "Understanding Emotional and Behavioral Relapse Before It Happens",
    shortDescription: "Relapse rarely occurs unexpectedly. Recognizing the three distinct stages—emotional, mental, and physical—empowers families and individuals to intervene early.",
    date: "August 12, 2026",
    readTime: "5 min read",
    author: "Dr. Sanad D. Pawar",
    authorRole: "Consultant Psychiatrist & Director",
    image: "https://images.unsplash.com/photo-1499209974431-9dddcece7f88?auto=format&fit=crop&w=1000&q=80",
    content: `
      ### The Anatomy of Relapse

      One of the most crucial insights in modern addiction medicine is that relapse is a multi-stage process rather than an isolated event. Long before a person physically consumes alcohol or a substance, subtle cognitive and emotional shifts begin taking place.

      #### 1. Emotional Relapse
      In this initial phase, the individual is not actively thinking about using substances. However, their emotional responses and behaviors set the stage:
      - Isolating from friends, support groups, or family
      - Bottling up emotions, irritability, and defensiveness
      - Neglecting basic self-care, sleep schedules, and nutrition
      - Skipping therapy sessions or aftercare meetings

      #### 2. Mental Relapse
      As emotional tension builds, a cognitive tug-of-war emerges:
      - Reminiscing about past substance use or minimizing past consequences
      - Bargaining ('I can just have one drink on a special occasion')
      - Seeking out old acquaintances or high-risk environments
      - Actively planning a relapse opportunity

      #### 3. Physical Relapse
      This is the actual act of using the substance. Once physical relapse occurs, re-entering a medically safe environment is vital to prevent full-blown addiction cycles.

      ### Proactive Intervention Strategies
      When caregivers or patients identify emotional relapse indicators, immediate connection with the treating clinical team, adjusting therapeutic routines, and open communication can halt progression effectively.
    `
  },
  {
    id: "supporting-loved-one-bipolar-disorder",
    slug: "supporting-loved-one-bipolar-disorder",
    category: "Family Guidance",
    title: "A Family Guide to Living with and Supporting Bipolar Disorder",
    shortDescription: "Practical, empathetic strategies for families navigating mood swings, medication adherence, and creating a stable, loving home environment.",
    date: "July 28, 2026",
    readTime: "7 min read",
    author: "Dr. Sanad D. Pawar",
    authorRole: "Consultant Psychiatrist & Director",
    image: "https://images.unsplash.com/photo-1511895426328-dc8714191300?auto=format&fit=crop&w=1000&q=80",
    content: `
      ### Beyond Mood Swings: Navigating Bipolar Disorder Together

      Bipolar Affective Disorder affects not just the patient, but the entire household. When families replace fear with scientific understanding, outcomes improve dramatically.

      #### Key Pillars of Family Support

      1. **Sleep Regularity is Sacred**: Circadian disruption is one of the strongest triggers for manic and depressive episodes. Encourage consistent bedtimes and wind-down rituals.
      2. **Separate the Person from the Illness**: During manic or irritable episodes, patients may make hurtful remarks. Recognizing that these behaviors stem from neurochemical deregulation helps reduce emotional friction.
      3. **Establish a Crisis Response Plan Ahead of Time**: When the patient is in a stable (euthymic) state, draft a mutual agreement detailing who to contact and what medical steps to take if acute symptoms re-emerge.
      4. **Care for the Caregiver**: Caregiver burnout is real. Ensure that primary caregivers have dedicated personal time, counseling, and rest.
    `
  },
  {
    id: "role-of-psychiatric-rehabilitation",
    slug: "role-of-psychiatric-rehabilitation",
    category: "Rehabilitation",
    title: "Why Psychiatric Rehabilitation is Essential for Long-Term Autonomy",
    shortDescription: "How structured cognitive retraining, daily living skills, and occupational therapy bridge hospital treatment and real-world flourishing.",
    date: "July 14, 2026",
    readTime: "6 min read",
    author: "Dr. Sanad D. Pawar",
    authorRole: "Consultant Psychiatrist & Director",
    image: "https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&w=1000&q=80",
    content: `
      ### Rebuilding Independence After Severe Mental Illness

      While pharmacotherapy plays a vital role in reducing psychiatric symptoms, medical stabilization alone does not automatically restore a person's ability to manage daily tasks, maintain employment, or sustain social connections.

      #### What Happens in Psychiatric Rehabilitation?
      - **Cognitive Remediation**: Specialized computer-assisted and group exercises to improve attention span, working memory, and executive function.
      - **Activities of Daily Living (ADL)**: Structured practice in personal grooming, money management, meal preparation, and time scheduling.
      - **Social Rhythm Therapy**: Re-establishing healthy interpersonal communication, assertiveness, and active listening.
      - **Vocational Readiness**: Exploring creative crafts, digital skills, or supported employment paths that give life a renewed sense of purpose.
    `
  },
  {
    id: "destigmatizing-psychiatric-inpatient-care",
    slug: "destigmatizing-psychiatric-inpatient-care",
    category: "Mental Health",
    title: "Breaking the Stigma: What Modern Inpatient Psychiatric Care Truly Looks Like",
    shortDescription: "Dispelling outdated myths and showcasing how modern therapeutic centres provide compassionate, dignified, resort-like healing environments.",
    date: "June 30, 2026",
    readTime: "4 min read",
    author: "Dr. Sanad D. Pawar",
    authorRole: "Consultant Psychiatrist & Director",
    image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=1000&q=80",
    content: `
      ### Transforming Mental Healthcare Spaces

      Outdated cinema and historical misconceptions often paint psychiatric hospitals as cold, restrictive institutions. The reality of modern psychiatric healthcare at UMED is completely the opposite.

      #### Modern Psychiatric Principles
      - **Architectural Healing**: Natural sunlight, open garden spaces, soothing acoustic design, and comfortable non-clinical aesthetics.
      - **Dignity & Autonomy**: Respecting individual choices, maintaining open communication with patients, and fostering collaborative care decisions.
      - **Multidisciplinary Synergy**: Daily interaction with psychiatrists, clinical psychologists, social workers, nutritionists, and yoga instructors.
      - **A Safe Sanctuary**: Providing a peaceful respite from overwhelming external life stressors to allow the brain and nervous system to recalibrate.
    `
  },
  {
    id: "mindfulness-somatic-grounding",
    slug: "mindfulness-somatic-grounding",
    category: "Mental Wellness",
    title: "Somatic Grounding Techniques to Regulate Severe Anxiety and Panic",
    shortDescription: "Scientific, bodily-focused practices that signal safety to your nervous system during acute sensory overwhelm or anxiety spikes.",
    date: "June 15, 2026",
    readTime: "5 min read",
    author: "Dr. Sanad D. Pawar",
    authorRole: "Consultant Psychiatrist & Director",
    image: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=1000&q=80",
    content: `
      ### Calming the Sympathetic Nervous System

      When anxiety or panic strikes, the sympathetic nervous system triggers the 'fight or flight' response, causing tachycardia, shallow breathing, and racing thoughts. Somatic grounding leverages physical sensations to restore parasympathetic calm.

      #### 3 Evidence-Based Grounding Practices
      1. **The 5-4-3-2-1 Sensory Method**:
         - Acknowledge 5 things you can see
         - 4 things you can physically touch
         - 3 things you can hear
         - 2 things you can smell
         - 1 thing you can taste
      2. **Physiological Sigh**: Two quick inhales through the nose followed by one long, slow exhale through the mouth. Repeating this 3–4 times immediately slows heart rate.
      3. **Weighted Grounding**: Placing feet firmly flat on the floor or resting hands firmly on the knees to reconnect with physical space.
    `
  }
];
